import React, { useEffect, useRef } from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { draggable, dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import DragHandleRoundedIcon from "@mui/icons-material/DragHandleRounded";

const ZoneOrderCard: React.FC<{
    id: string;
    name: string | null;
    isDragging: boolean;
    /** When true, dragging is disabled and the card appears dimmed. */
    disableDragging?: boolean;
    onHover: (id: string | null) => void;
    onDragStart: () => void;
    onDrop: () => void;
    onDropTargetEnter: () => void;
}> = ({ id, name, isDragging, disableDragging = false, onHover, onDrop, onDragStart, onDropTargetEnter }) => {
    const cardRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!cardRef.current || disableDragging) return;

        return draggable({
            element: cardRef.current,
            getInitialData: () => ({ id }),
            onDragStart,
            onDrop,
        });
    }, [id, disableDragging, onDragStart, onDrop]);

    useEffect(() => {
        if (!cardRef.current || disableDragging) return;

        return dropTargetForElements({
            element: cardRef.current,
            canDrop: () => true,
            onDragEnter: onDropTargetEnter,
        });
    }, [disableDragging, onDropTargetEnter]);

    return (
        <React.Fragment>
            <div onMouseOver={() => onHover(id)} onMouseOut={() => onHover(null)}>
                <Stack
                    direction="row"
                    ref={cardRef}
                    p={1}
                    sx={(theme) => ({
                        cursor: !disableDragging ? "grab" : "default",
                        touchAction: !disableDragging ? "none" : "auto",
                        ":hover": { backgroundColor: theme.palette.action.hover },
                        opacity: isDragging ? 0 : !disableDragging ? 1 : 0.5,
                    })}
                    spacing={2}
                >
                    <DragHandleRoundedIcon sx={{ opacity: !disableDragging ? 1 : 0.3 }} />
                    <Typography>{name}</Typography>
                </Stack>
            </div>
        </React.Fragment>
    );
};

export default ZoneOrderCard;
